/**
 * @file The bake's ray-casting acceleration structure: a 2D XZ bucket grid over
 * axis-aligned boxes (plus optional exact polygon prisms), marched with a DDA.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A 2D GRID AND NOT A BVH
 * ─────────────────────────────────────────────────────────────────────────────
 * A city is a heightfield of extrusions: almost all of the interesting variation
 * is in XZ, and Y is answered by one number per cell (`cellMaxY`). That gives
 * two cheap wins a BVH does not:
 *   • a ray whose Y over a cell's t-span is already above `cellMaxY` skips the
 *     cell without touching a single box;
 *   • an upward ray (every sun/AO ray) that climbs above the WHOLE grid's max Y
 *     terminates the march outright. Sun rays in a 3 m-tall test grid die after
 *     ~2 cells.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CONVENTIONS
 * ─────────────────────────────────────────────────────────────────────────────
 * • ZERO three.js imports. This module is cloned into `bake.worker.js`'s import
 *   graph, and the worker must not drag the renderer in.
 * • Directions passed to {@link OccluderSet#rayT} are assumed UNIT length; `maxT`
 *   is therefore in metres.
 * • The CALLER pushes the ray origin off the surface — `p + 0.05 * n` is what
 *   `bakeVertexLight` uses. Boxes are slab-tested with `tmin > 1e-2`, so a
 *   vertex exactly on its own occluder does not self-shadow, but a vertex INSIDE
 *   a box is not detected as occluded (there is no "inside" test by design: the
 *   ground plate under a building is legitimately inside nothing).
 *
 * @module engine/bake/Occluders
 */

/** @typedef {import('../types.js').Collider} Collider */
/** @typedef {{x:number,y:number,z:number}|[number,number,number]} V3Like */

const EPS = 1e-9;
/** Hits closer than this are the surface the ray started on. */
const T_MIN = 1e-2;
const TAU = Math.PI * 2;

const vx = (v) => (Array.isArray(v) ? v[0] : v.x);
const vy = (v) => (Array.isArray(v) ? v[1] : v.y);
const vz = (v) => (Array.isArray(v) ? v[2] : v.z);

/**
 * Van der Corput radical inverse base 2 — the low-discrepancy sequence used
 * whenever a caller does not supply an `rng` (keeps `sunVis` deterministic).
 * @param {number} i
 * @returns {number} in [0,1)
 */
function radicalInverse2(i) {
  let bits = i >>> 0;
  bits = ((bits << 16) | (bits >>> 16)) >>> 0;
  bits = (((bits & 0x55555555) << 1) | ((bits & 0xaaaaaaaa) >>> 1)) >>> 0;
  bits = (((bits & 0x33333333) << 2) | ((bits & 0xcccccccc) >>> 2)) >>> 0;
  bits = (((bits & 0x0f0f0f0f) << 4) | ((bits & 0xf0f0f0f0) >>> 4)) >>> 0;
  bits = (((bits & 0x00ff00ff) << 8) | ((bits & 0xff00ff00) >>> 8)) >>> 0;
  return bits / 4294967296;
}

/**
 * Branchless orthonormal basis (Duff et al. 2017). Writes the two tangents into
 * `out` as `[tx,ty,tz, bx,by,bz]`.
 * @param {number} nx @param {number} ny @param {number} nz
 * @param {Float64Array|number[]} out length >= 6
 */
export function onb(nx, ny, nz, out) {
  const sign = nz >= 0 ? 1 : -1;
  const a = -1 / (sign + nz);
  const b = nx * ny * a;
  out[0] = 1 + sign * nx * nx * a;
  out[1] = sign * b;
  out[2] = -sign * nx;
  out[3] = b;
  out[4] = sign + ny * ny * a;
  out[5] = -ny;
  return out;
}

export class OccluderSet {
  constructor() {
    /** @type {number[]} 6 numbers per occluder: minX,minY,minZ,maxX,maxY,maxZ */
    this._raw = [];
    /** @type {number[]} index into `_rawPolys`, or -1 for a plain box */
    this._rawPoly = [];
    /** @type {number[][]} flat `[x,z, x,z, ...]` footprints */
    this._rawPolys = [];

    this._packed = false;
    this._count = 0;
    this.cell = 4;

    // Packed (post-`build`) state.
    /** @type {Float32Array} */
    this._boxes = new Float32Array(0);
    /** @type {Int32Array} */
    this._polyIdx = new Int32Array(0);
    /** @type {Int32Array} */
    this._polyOff = new Int32Array(1);
    /** @type {Float32Array} */
    this._polyPts = new Float32Array(0);
    /** @type {Int32Array} CSR row starts, length nx*nz+1 */
    this._cellStart = new Int32Array(1);
    /** @type {Int32Array} */
    this._cellItems = new Int32Array(0);
    /** @type {Float32Array} highest occluder top in each cell */
    this._cellMaxY = new Float32Array(0);
    /** @type {Int32Array} per-occluder visit stamp, so a box spanning several
     *  cells is slab-tested once per ray. */
    this._stamp = new Int32Array(0);
    this._stampId = 0;

    this._minX = 0;
    this._minZ = 0;
    this._nx = 1;
    this._nz = 1;
    this._maxY = -Infinity;
    this._minY = Infinity;
    this._diag = 0;

    this._tmpOnb = new Float64Array(6);
  }

  /** @returns {number} Number of occluders added. */
  get count() {
    return this._packed ? this._count : this._raw.length / 6;
  }

  /**
   * @returns {{minX:number,minZ:number,maxX:number,maxZ:number,minY:number,maxY:number}}
   *   Grid extent (cell-aligned in XZ). All zero when the set is empty.
   */
  get bounds() {
    if (!this._packed) this.build({ cell: this.cell });
    if (!this._count) return { minX: 0, minZ: 0, maxX: 0, maxZ: 0, minY: 0, maxY: 0 };
    return {
      minX: this._minX,
      minZ: this._minZ,
      maxX: this._minX + this._nx * this.cell,
      maxZ: this._minZ + this._nz * this.cell,
      minY: this._minY,
      maxY: this._maxY,
    };
  }

  // ── authoring ──────────────────────────────────────────────────────────────

  /**
   * @param {V3Like} minV3
   * @param {V3Like} maxV3
   * @returns {this}
   */
  addBox(minV3, maxV3) {
    const x0 = vx(minV3);
    const y0 = vy(minV3);
    const z0 = vz(minV3);
    const x1 = vx(maxV3);
    const y1 = vy(maxV3);
    const z1 = vz(maxV3);
    this._raw.push(
      Math.min(x0, x1), Math.min(y0, y1), Math.min(z0, z1),
      Math.max(x0, x1), Math.max(y0, y1), Math.max(z0, z1),
    );
    this._rawPoly.push(-1);
    this._packed = false;
    return this;
  }

  /**
   * A vertical extrusion of a footprint. Stores the AABB (used by the grid and
   * by the broad phase) AND the polygon, so the narrow phase can be exact for
   * L-shapes, chamfered corners and rotated rectangles — which a fat AABB would
   * over-shadow badly.
   *
   * @param {Array<[number,number]>} poly World XZ, either winding.
   * @param {number} y0 Bottom.
   * @param {number} y1 Top.
   * @returns {this}
   */
  addPrism(poly, y0, y1) {
    if (!poly || poly.length < 3) return this;
    let minX = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxZ = -Infinity;
    /** @type {number[]} */
    const flat = [];
    for (const p of poly) {
      const x = Array.isArray(p) ? p[0] : p.x;
      const z = Array.isArray(p) ? p[1] : p.z;
      flat.push(x, z);
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }
    this._raw.push(minX, Math.min(y0, y1), minZ, maxX, Math.max(y0, y1), maxZ);
    this._rawPoly.push(this._rawPolys.length);
    this._rawPolys.push(flat);
    this._packed = false;
    return this;
  }

  /**
   * Bulk-load a level's collision set. `'aabb'` colliders become boxes;
   * `'circle'` colliders become a square of half-extent `r * 0.8` — a circle's
   * true AABB over-shadows by ~27%, and 0.8 is the area-matched compromise
   * (trees are the main client and their canopies get a soft blob decal anyway).
   *
   * Colliders with an infinite/missing height use `defaultH`.
   *
   * @param {Collider[]} colliders
   * @param {number} [defaultH=6]
   * @param {{skipTags?: string[]}} [opts] e.g. `{skipTags:['boundary','water']}`
   *   — invisible gameplay volumes must not cast shadows.
   * @returns {this}
   */
  fromColliders(colliders, defaultH = 6, opts = {}) {
    const skip = opts.skipTags ? new Set(opts.skipTags) : null;
    for (const c of colliders || []) {
      if (!c) continue;
      if (skip && c.tag && skip.has(c.tag)) continue;
      const y0 = c.y ?? 0;
      const h = Number.isFinite(c.h) ? /** @type {number} */ (c.h) : defaultH;
      if (h <= 0) continue;
      if (c.kind === 'aabb') {
        this.addBox({ x: c.minX, y: y0, z: c.minZ }, { x: c.maxX, y: y0 + h, z: c.maxZ });
      } else if (c.kind === 'circle') {
        const r = c.r * 0.8;
        this.addBox({ x: c.x - r, y: y0, z: c.z - r }, { x: c.x + r, y: y0 + h, z: c.z + r });
      }
    }
    return this;
  }

  // ── build ──────────────────────────────────────────────────────────────────

  /**
   * Packs everything into typed arrays and buckets occluders into a `cell`-metre
   * XZ grid. Idempotent; called lazily by the query methods.
   * @param {{cell?: number}} [opts]
   * @returns {this}
   */
  build({ cell = 4 } = {}) {
    const n = this._raw.length / 6;
    this.cell = Math.max(0.5, cell);
    this._count = n;
    this._packed = true;

    if (n === 0) {
      this._boxes = new Float32Array(0);
      this._polyIdx = new Int32Array(0);
      this._cellStart = new Int32Array(2);
      this._cellItems = new Int32Array(0);
      this._cellMaxY = new Float32Array(1).fill(-Infinity);
      this._stamp = new Int32Array(0);
      this._nx = 1;
      this._nz = 1;
      this._minX = 0;
      this._minZ = 0;
      this._minY = 0;
      this._maxY = 0;
      this._diag = 0;
      return this;
    }

    const boxes = new Float32Array(this._raw);
    this._boxes = boxes;
    this._polyIdx = new Int32Array(this._rawPoly);
    this._stamp = new Int32Array(n);
    this._stampId = 0;

    // Polygon table -> CSR.
    let ptTotal = 0;
    for (const p of this._rawPolys) ptTotal += p.length;
    this._polyOff = new Int32Array(this._rawPolys.length + 1);
    this._polyPts = new Float32Array(ptTotal);
    let w = 0;
    for (let i = 0; i < this._rawPolys.length; i++) {
      this._polyOff[i] = w;
      this._polyPts.set(this._rawPolys[i], w);
      w += this._rawPolys[i].length;
    }
    this._polyOff[this._rawPolys.length] = w;

    // Extent.
    let minX = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxZ = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < n; i++) {
      const o = i * 6;
      if (boxes[o] < minX) minX = boxes[o];
      if (boxes[o + 1] < minY) minY = boxes[o + 1];
      if (boxes[o + 2] < minZ) minZ = boxes[o + 2];
      if (boxes[o + 3] > maxX) maxX = boxes[o + 3];
      if (boxes[o + 4] > maxY) maxY = boxes[o + 4];
      if (boxes[o + 5] > maxZ) maxZ = boxes[o + 5];
    }
    const c = this.cell;
    this._minX = Math.floor(minX / c) * c;
    this._minZ = Math.floor(minZ / c) * c;
    this._nx = Math.max(1, Math.ceil((maxX - this._minX) / c));
    this._nz = Math.max(1, Math.ceil((maxZ - this._minZ) / c));
    this._minY = minY;
    this._maxY = maxY;
    this._diag = Math.hypot(this._nx * c, this._nz * c);

    const nCells = this._nx * this._nz;
    const counts = new Int32Array(nCells);
    const cellMaxY = new Float32Array(nCells).fill(-Infinity);

    // Pass 1: count + cellMaxY.
    for (let i = 0; i < n; i++) {
      const o = i * 6;
      const gx0 = this._gx(boxes[o]);
      const gx1 = this._gx(boxes[o + 3]);
      const gz0 = this._gz(boxes[o + 2]);
      const gz1 = this._gz(boxes[o + 5]);
      const top = boxes[o + 4];
      for (let gz = gz0; gz <= gz1; gz++) {
        for (let gx = gx0; gx <= gx1; gx++) {
          const ci = gz * this._nx + gx;
          counts[ci]++;
          if (top > cellMaxY[ci]) cellMaxY[ci] = top;
        }
      }
    }

    const start = new Int32Array(nCells + 1);
    let acc = 0;
    for (let i = 0; i < nCells; i++) {
      start[i] = acc;
      acc += counts[i];
    }
    start[nCells] = acc;

    const items = new Int32Array(acc);
    const cursor = start.slice(0, nCells);
    for (let i = 0; i < n; i++) {
      const o = i * 6;
      const gx0 = this._gx(boxes[o]);
      const gx1 = this._gx(boxes[o + 3]);
      const gz0 = this._gz(boxes[o + 2]);
      const gz1 = this._gz(boxes[o + 5]);
      for (let gz = gz0; gz <= gz1; gz++) {
        for (let gx = gx0; gx <= gx1; gx++) {
          const ci = gz * this._nx + gx;
          items[cursor[ci]++] = i;
        }
      }
    }

    this._cellStart = start;
    this._cellItems = items;
    this._cellMaxY = cellMaxY;
    return this;
  }

  /** @param {number} x @returns {number} clamped grid column */
  _gx(x) {
    const i = Math.floor((x - this._minX) / this.cell);
    return i < 0 ? 0 : i >= this._nx ? this._nx - 1 : i;
  }

  /** @param {number} z @returns {number} clamped grid row */
  _gz(z) {
    const i = Math.floor((z - this._minZ) / this.cell);
    return i < 0 ? 0 : i >= this._nz ? this._nz - 1 : i;
  }

  // ── queries ────────────────────────────────────────────────────────────────

  /**
   * Highest occluder top over the 4 m cell containing `(x,z)`. Conservative by
   * construction (it is a per-CELL maximum, not a per-point one).
   * @param {number} x @param {number} z
   * @returns {number} `-Infinity` when the cell is empty or outside the grid.
   */
  maxHeightAt(x, z) {
    if (!this._packed) this.build({ cell: this.cell });
    if (!this._count) return -Infinity;
    const c = this.cell;
    const gx = Math.floor((x - this._minX) / c);
    const gz = Math.floor((z - this._minZ) / c);
    if (gx < 0 || gz < 0 || gx >= this._nx || gz >= this._nz) return -Infinity;
    return this._cellMaxY[gz * this._nx + gx];
  }

  /**
   * Is the point INSIDE an occluder? Rays deliberately ignore the box they
   * start inside (see the header), so anything that samples free space —
   * {@link import('./LightProbeGrid.js').LightProbeGrid} above all — has to ask
   * this separately or a probe buried in a building reads as fully sunlit.
   *
   * @param {number} x @param {number} y @param {number} z
   * @returns {boolean}
   */
  contains(x, y, z) {
    if (!this._packed) this.build({ cell: this.cell });
    if (!this._count) return false;
    const c = this.cell;
    const gx = Math.floor((x - this._minX) / c);
    const gz = Math.floor((z - this._minZ) / c);
    if (gx < 0 || gz < 0 || gx >= this._nx || gz >= this._nz) return false;
    const ci = gz * this._nx + gx;
    if (y > this._cellMaxY[ci]) return false;
    const B = this._boxes;
    for (let k = this._cellStart[ci]; k < this._cellStart[ci + 1]; k++) {
      const i = this._cellItems[k];
      const o = i * 6;
      if (x < B[o] || x > B[o + 3] || y < B[o + 1] || y > B[o + 4] || z < B[o + 2] || z > B[o + 5]) continue;
      const pi = this._polyIdx[i];
      if (pi < 0) return true;
      const n = (this._polyOff[pi + 1] - this._polyOff[pi]) / 2;
      if (this._insidePoly(this._polyOff[pi], n, x, z)) return true;
    }
    return false;
  }

  /**
   * Closest occluder hit along the ray.
   *
   * @param {number} ox @param {number} oy @param {number} oz Origin — already
   *   pushed off the surface by the caller (`+0.05 * n`).
   * @param {number} dx @param {number} dy @param {number} dz UNIT direction.
   * @param {number} [maxT=Infinity] Metres.
   * @returns {number} Distance to the hit, or `-1` for a miss.
   */
  rayT(ox, oy, oz, dx, dy, dz, maxT = Infinity) {
    if (!this._packed) this.build({ cell: this.cell });
    if (!this._count) return -1;

    const c = this.cell;
    const nx = this._nx;
    const nz = this._nz;
    const gMinX = this._minX;
    const gMinZ = this._minZ;
    const gMaxX = gMinX + nx * c;
    const gMaxZ = gMinZ + nz * c;

    // A ray that never rises/falls into the occluder Y band can be rejected
    // before any traversal at all.
    if (dy >= 0 && oy > this._maxY) return -1;
    if (dy <= 0 && oy < this._minY) return -1;

    // Clip the ray to the grid's XZ extent.
    let t0 = 0;
    let t1 = maxT;
    if (Math.abs(dx) < EPS) {
      if (ox < gMinX || ox > gMaxX) return -1;
    } else {
      let ta = (gMinX - ox) / dx;
      let tb = (gMaxX - ox) / dx;
      if (ta > tb) {
        const s = ta;
        ta = tb;
        tb = s;
      }
      if (ta > t0) t0 = ta;
      if (tb < t1) t1 = tb;
    }
    if (Math.abs(dz) < EPS) {
      if (oz < gMinZ || oz > gMaxZ) return -1;
    } else {
      let ta = (gMinZ - oz) / dz;
      let tb = (gMaxZ - oz) / dz;
      if (ta > tb) {
        const s = ta;
        ta = tb;
        tb = s;
      }
      if (ta > t0) t0 = ta;
      if (tb < t1) t1 = tb;
    }
    if (t0 > t1) return -1;

    const stamp = ++this._stampId;

    // Degenerate horizontal direction (a straight-up probe ray): one cell.
    if (Math.abs(dx) < EPS && Math.abs(dz) < EPS) {
      const gx = this._gx(ox);
      const gz = this._gz(oz);
      return this._testCell(gz * nx + gx, ox, oy, oz, dx, dy, dz, maxT, stamp, -1);
    }

    let t = t0;
    let px = ox + dx * t;
    let pz = oz + dz * t;
    let ix = this._gx(px);
    let iz = this._gz(pz);

    let stepX = 0;
    let tMaxX = Infinity;
    let tDeltaX = Infinity;
    if (dx > EPS) {
      stepX = 1;
      tDeltaX = c / dx;
      tMaxX = t + (gMinX + (ix + 1) * c - px) / dx;
    } else if (dx < -EPS) {
      stepX = -1;
      tDeltaX = -c / dx;
      tMaxX = t + (gMinX + ix * c - px) / dx;
    }

    let stepZ = 0;
    let tMaxZ = Infinity;
    let tDeltaZ = Infinity;
    if (dz > EPS) {
      stepZ = 1;
      tDeltaZ = c / dz;
      tMaxZ = t + (gMinZ + (iz + 1) * c - pz) / dz;
    } else if (dz < -EPS) {
      stepZ = -1;
      tDeltaZ = -c / dz;
      tMaxZ = t + (gMinZ + iz * c - pz) / dz;
    }

    let best = -1;
    let guard = (nx + nz) * 2 + 8;

    while (guard-- > 0) {
      const tExit = Math.min(tMaxX, tMaxZ, t1);
      const ci = iz * nx + ix;
      const cMaxY = this._cellMaxY[ci];

      if (cMaxY > -Infinity) {
        // Fast path: if the ray's LOWEST Y across this cell's span is already
        // above everything in the cell, no box in it can be hit.
        const yA = oy + dy * t;
        const yB = oy + dy * tExit;
        if ((yA < yB ? yA : yB) <= cMaxY) {
          const hit = this._testCell(ci, ox, oy, oz, dx, dy, dz, maxT, stamp, best);
          if (hit >= 0 && (best < 0 || hit < best)) best = hit;
          // Hits inside this cell's span are final — DDA order guarantees it.
          if (best >= 0 && best <= tExit + 1e-6) return best;
        }
      }

      // Climbed above (or sank below) the whole structure: nothing left to hit.
      const yEnd = oy + dy * tExit;
      if (dy > 0 && yEnd > this._maxY) break;
      if (dy < 0 && yEnd < this._minY) break;
      if (tExit >= t1) break;

      if (tMaxX < tMaxZ) {
        ix += stepX;
        t = tMaxX;
        tMaxX += tDeltaX;
        if (ix < 0 || ix >= nx) break;
      } else {
        iz += stepZ;
        t = tMaxZ;
        tMaxZ += tDeltaZ;
        if (iz < 0 || iz >= nz) break;
      }
    }

    return best;
  }

  /**
   * @param {number} ox @param {number} oy @param {number} oz
   * @param {number} dx @param {number} dy @param {number} dz UNIT direction.
   * @param {number} [maxT=Infinity]
   * @returns {boolean}
   */
  rayHit(ox, oy, oz, dx, dy, dz, maxT = Infinity) {
    return this.rayT(ox, oy, oz, dx, dy, dz, maxT) >= 0;
  }

  /**
   * Fraction of a small cone around `sunDir` that reaches the point. This is the
   * whole "no shadow maps" trick: a handful of jittered rays to a FIXED sun.
   *
   * @param {number} px @param {number} py @param {number} pz Origin — push it
   *   off the surface yourself (`p + 0.05 * n`).
   * @param {V3Like} sunDir Normalised, surface -> sun.
   * @param {{rays?: number, cone?: number, rng?: () => number, maxT?: number}} [opts]
   *   `cone` is the HALF-ANGLE in radians (0.06 rad ~= 3.5 deg, the plan's
   *   `sunConeDeg`). Omit `rng` for a deterministic low-discrepancy cone.
   * @returns {number} 0 (fully shadowed) .. 1 (open sky).
   */
  sunVis(px, py, pz, sunDir, opts = {}) {
    const rays = Math.max(1, opts.rays ?? 4);
    const cone = opts.cone ?? 0.06;
    const rng = opts.rng ?? null;
    if (!this._packed) this.build({ cell: this.cell });
    if (!this._count) return 1;
    const maxT = opts.maxT ?? Math.max(this._diag, 1);

    const lx = vx(sunDir);
    const ly = vy(sunDir);
    const lz = vz(sunDir);
    const ob = onb(lx, ly, lz, this._tmpOnb);
    const tx = ob[0];
    const ty = ob[1];
    const tz = ob[2];
    const bx = ob[3];
    const by = ob[4];
    const bz = ob[5];

    let open = 0;
    for (let i = 0; i < rays; i++) {
      const u1 = rng ? rng() : (i + 0.5) / rays;
      const u2 = rng ? rng() : radicalInverse2(i);
      // Uniform inside the cone's disc; sqrt keeps the samples area-weighted.
      const a = cone * Math.sqrt(u1);
      const phi = TAU * u2;
      const sa = Math.sin(a);
      const ca = Math.cos(a);
      const cp = Math.cos(phi) * sa;
      const sp = Math.sin(phi) * sa;
      let dx = lx * ca + tx * cp + bx * sp;
      let dy = ly * ca + ty * cp + by * sp;
      let dz = lz * ca + tz * cp + bz * sp;
      const inv = 1 / (Math.hypot(dx, dy, dz) || 1);
      dx *= inv;
      dy *= inv;
      dz *= inv;
      if (this.rayT(px, py, pz, dx, dy, dz, maxT) < 0) open++;
    }
    return open / rays;
  }

  // ── narrow phase ───────────────────────────────────────────────────────────

  /**
   * Slab-tests every occluder bucketed in one cell.
   * @param {number} ci Cell index.
   * @param {number} ox @param {number} oy @param {number} oz
   * @param {number} dx @param {number} dy @param {number} dz
   * @param {number} maxT
   * @param {number} stamp Per-ray stamp so a box in several cells is tested once.
   * @param {number} best Current best hit (`-1` for none).
   * @returns {number} Best hit distance found in this cell, or `-1`.
   */
  _testCell(ci, ox, oy, oz, dx, dy, dz, maxT, stamp, best) {
    const start = this._cellStart[ci];
    const end = this._cellStart[ci + 1];
    const items = this._cellItems;
    const B = this._boxes;
    const stampArr = this._stamp;
    let out = -1;

    for (let k = start; k < end; k++) {
      const i = items[k];
      if (stampArr[i] === stamp) continue;
      stampArr[i] = stamp;

      const o = i * 6;
      let tmin = 0;
      let tmax = maxT;

      if (dx > EPS || dx < -EPS) {
        let ta = (B[o] - ox) / dx;
        let tb = (B[o + 3] - ox) / dx;
        if (ta > tb) {
          const s = ta;
          ta = tb;
          tb = s;
        }
        if (ta > tmin) tmin = ta;
        if (tb < tmax) tmax = tb;
        if (tmin > tmax) continue;
      } else if (ox < B[o] || ox > B[o + 3]) continue;

      if (dy > EPS || dy < -EPS) {
        let ta = (B[o + 1] - oy) / dy;
        let tb = (B[o + 4] - oy) / dy;
        if (ta > tb) {
          const s = ta;
          ta = tb;
          tb = s;
        }
        if (ta > tmin) tmin = ta;
        if (tb < tmax) tmax = tb;
        if (tmin > tmax) continue;
      } else if (oy < B[o + 1] || oy > B[o + 4]) continue;

      if (dz > EPS || dz < -EPS) {
        let ta = (B[o + 2] - oz) / dz;
        let tb = (B[o + 5] - oz) / dz;
        if (ta > tb) {
          const s = ta;
          ta = tb;
          tb = s;
        }
        if (ta > tmin) tmin = ta;
        if (tb < tmax) tmax = tb;
        if (tmin > tmax) continue;
      } else if (oz < B[o + 2] || oz > B[o + 5]) continue;

      if (tmin <= T_MIN) continue; // the surface we started on (or inside)
      if (out >= 0 && tmin >= out) continue;
      if (best >= 0 && tmin >= best) continue;

      const pi = this._polyIdx[i];
      const t = pi < 0 ? tmin : this._prismT(pi, ox, oz, dx, dz, tmin, tmax);
      if (t < 0) continue;
      if (out < 0 || t < out) out = t;
    }
    return out;
  }

  /**
   * Narrow phase for a footprint prism: the AABB test already clipped the ray to
   * the Y slab and the bounding rect, so all that is left is "when is the XZ ray
   * inside the polygon".
   *
   * @param {number} pi Polygon index.
   * @param {number} ox @param {number} oz
   * @param {number} dx @param {number} dz
   * @param {number} tmin @param {number} tmax Clipped slab interval.
   * @returns {number} Entry distance, or `-1`.
   */
  _prismT(pi, ox, oz, dx, dz, tmin, tmax) {
    const off = this._polyOff[pi];
    const end = this._polyOff[pi + 1];
    const P = this._polyPts;
    const n = (end - off) / 2;

    // Already inside at the slab entry point?
    if (this._insidePoly(off, n, ox + dx * tmin, oz + dz * tmin)) {
      return tmin > T_MIN ? tmin : -1;
    }

    // Otherwise the first edge crossing inside the slab wins.
    let best = -1;
    for (let i = 0; i < n; i++) {
      const a = off + i * 2;
      const b = off + ((i + 1) % n) * 2;
      const ex = P[b] - P[a];
      const ez = P[b + 1] - P[a + 1];
      const den = dx * ez - dz * ex;
      if (Math.abs(den) < EPS) continue;
      const rx = P[a] - ox;
      const rz = P[a + 1] - oz;
      const t = (rx * ez - rz * ex) / den;
      const u = (rx * dz - rz * dx) / den;
      if (u < 0 || u > 1) continue;
      if (t <= Math.max(tmin, T_MIN) || t > tmax) continue;
      if (best < 0 || t < best) best = t;
    }
    return best;
  }

  /**
   * Crossing-number test against a flat `[x,z,...]` run.
   * @param {number} off @param {number} n @param {number} x @param {number} z
   * @returns {boolean}
   */
  _insidePoly(off, n, x, z) {
    const P = this._polyPts;
    let inside = false;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = P[off + i * 2];
      const zi = P[off + i * 2 + 1];
      const xj = P[off + j * 2];
      const zj = P[off + j * 2 + 1];
      if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
    }
    return inside;
  }

  // ── worker transport ───────────────────────────────────────────────────────

  /**
   * Flattens the BUILT structure to plain typed arrays. `postMessage` cannot
   * carry a class instance, so the worker rebuilds with {@link OccluderSet.fromArrays}
   * instead of re-bucketing.
   *
   * The arrays are the live internals — structured-CLONE them (do not list them
   * as transferables) or this set is neutered on the main thread.
   *
   * @returns {Object} plain, cloneable
   */
  toArrays() {
    if (!this._packed) this.build({ cell: this.cell });
    return {
      cell: this.cell,
      count: this._count,
      nx: this._nx,
      nz: this._nz,
      minX: this._minX,
      minZ: this._minZ,
      minY: this._minY,
      maxY: this._maxY,
      boxes: this._boxes,
      polyIdx: this._polyIdx,
      polyOff: this._polyOff,
      polyPts: this._polyPts,
      cellStart: this._cellStart,
      cellItems: this._cellItems,
      cellMaxY: this._cellMaxY,
    };
  }

  /**
   * @param {ReturnType<OccluderSet['toArrays']>|null|undefined} o
   * @returns {OccluderSet|null} `null` for null input, so the worker can treat
   *   "no occluders" and "occluders omitted" the same way.
   */
  static fromArrays(o) {
    if (!o) return null;
    const s = new OccluderSet();
    s._packed = true;
    s.cell = o.cell;
    s._count = o.count;
    s._nx = o.nx;
    s._nz = o.nz;
    s._minX = o.minX;
    s._minZ = o.minZ;
    s._minY = o.minY;
    s._maxY = o.maxY;
    s._boxes = o.boxes;
    s._polyIdx = o.polyIdx;
    s._polyOff = o.polyOff;
    s._polyPts = o.polyPts;
    s._cellStart = o.cellStart;
    s._cellItems = o.cellItems;
    s._cellMaxY = o.cellMaxY;
    s._stamp = new Int32Array(o.count);
    s._diag = Math.hypot(o.nx * o.cell, o.nz * o.cell);
    return s;
  }
}
